<template>
  <b-modal :active.sync="isActive" :can-cancel="false" has-modal-card>
    <section class="modal-card-body inactivity-modal">
      <div class="icon-container">
        <b-icon icon="hand" size="is-large" type="is-danger" />
      </div>
      <h2 class="title is-4 has-text-centered">¿Sigues ahí?</h2>
      <p class="has-text-centered mt-2 mb-4">
        Por seguridad, cerraremos tu sesión en:
        <br>
        <b class="countdown-timer">⏰ -{{ minutesLeft }}</b>
      </p>
      <div class="buttons is-centered">
        <b-button type="is-danger" @click="$emit('logout')"
          >Cerrar sesión</b-button
        >
        <b-button type="is-warning" @click="$emit('continue')"
          >Estoy aquí</b-button
        >
      </div>
    </section>
  </b-modal>
</template>

<script>
import { clearSession } from '@/axiosConfig'

export default {
  name: 'InactivityModal',
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      logoutTimeoutId: null,
      countdownIntervalId: null,
      currentSecondsLeft: 0,
      logoutCheckIntervalId: null,
    }
  },
  computed: {
    minutesLeft() {
      const secondsLeft = this.currentSecondsLeft

      if (secondsLeft <= 0) return '0:00'

      // Convertir a formato MM:SS
      const minutes = Math.floor(secondsLeft / 60)
      const seconds = secondsLeft % 60
      
      // Formatear con ceros a la izquierda
      const formattedMinutes = minutes.toString()
      const formattedSeconds = seconds.toString().padStart(2, '0')
      
      return `${formattedMinutes}:${formattedSeconds}`
    },
    secondsLeft() {
      const tokenRaw = localStorage.getItem('x-token')
      if (!tokenRaw) return 0
      const token = tokenRaw.replace(/^Bearer\s+/, '')
      let payload
      try {
        payload = JSON.parse(atob(token.split('.')[1]))
      } catch (e) {
        return 0
      }
      
      const now = Math.floor(Date.now() / 1000)
      const exp = payload.exp
      const receivedMs = localStorage.getItem('x-token-received')
      
      if (!receivedMs) {
        // Fallback al cálculo original si no hay timestamp de recepción
        const secondsLeft = exp - now
        return secondsLeft > 0 ? secondsLeft : 0
      }

      // USAR LA MISMA LÓGICA QUE session-activity.js
      const received = Math.floor(Number(receivedMs) / 1000)
      const totalTime = exp - received
      const elapsed = now - received
      const secondsLeft = exp - now
      return secondsLeft > 0 ? secondsLeft : 0
    },
  },
  watch: {
    isActive(newVal) {
      if (newVal) {
        // Cuando el modal se muestra, iniciar la cuenta regresiva y la verificación de expiración
        this.startCountdown()
        this.setAutoLogout()
      } else {
        // Cuando el modal se oculta, limpiar la cuenta regresiva y la verificación de expiración
        this.stopCountdown()
        this.clearAutoLogout()
      }
    },
  },
  methods: {
    startCountdown() {
      this.stopCountdown()
      
      // Inicializar con el valor actual del token
      this.currentSecondsLeft = this.secondsLeft
      
      // Actualizar cada segundo
      this.countdownIntervalId = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    },
    
    stopCountdown() {
      if (this.countdownIntervalId) {
        clearInterval(this.countdownIntervalId)
        this.countdownIntervalId = null
      }
    },
    
    updateCountdown() {
      // Decrementar el tiempo restante
      if (this.currentSecondsLeft > 0) {
        this.currentSecondsLeft--
      }
      
      // Si llegó a cero, detener
      if (this.currentSecondsLeft <= 0) {
        this.currentSecondsLeft = 0
        this.stopCountdown()
        return
      }
      
      // Cada 10 segundos, recalcular desde el token para mantener sincronización
      if (this.currentSecondsLeft % 10 === 0) {
        this.syncWithToken()
      }
    },

    syncWithToken() {
      const tokenRaw = localStorage.getItem('x-token')
      if (!tokenRaw) return

      const token = tokenRaw.replace(/^Bearer\s+/, '')
      let payload
      try {
        payload = JSON.parse(atob(token.split('.')[1]))
      } catch (e) {
        return
      }
      
      const now = Math.floor(Date.now() / 1000)
      const exp = payload.exp
      const realSecondsLeft = exp - now
      
      // Sincronizar solo si hay una diferencia significativa (más de 2 segundos)
      if (Math.abs(realSecondsLeft - this.currentSecondsLeft) > 2) {
        this.currentSecondsLeft = realSecondsLeft > 0 ? realSecondsLeft : 0
      }
    },
    
    setAutoLogout() {
      this.clearAutoLogout()
      
      // En lugar de timeout fijo, verificar cada segundo si el token ha expirado
      this.logoutCheckIntervalId = setInterval(() => {
        this.checkTokenExpiration()
      }, 1000)
    },
    
    checkTokenExpiration() {
      const tokenRaw = localStorage.getItem('x-token')
      if (!tokenRaw) {
        this.performLogout()
        return
      }

      const token = tokenRaw.replace(/^Bearer\s+/, '')
      let payload
      try {
        payload = JSON.parse(atob(token.split('.')[1]))
      } catch (e) {
        this.performLogout()
        return
      }
      
      const now = Math.floor(Date.now() / 1000)
      const exp = payload.exp
      const secondsLeft = exp - now
      
      // Solo redireccionar si el token REALMENTE ha expirado
      if (secondsLeft <= 0) {
        this.performLogout()
        return
      }
    },
    
    performLogout() {
      this.clearAutoLogout()
      clearSession()
      location.href = '/view/login'
    },
    
    clearAutoLogout() {
      if (this.logoutCheckIntervalId) {
        clearInterval(this.logoutCheckIntervalId)
        this.logoutCheckIntervalId = null
      }
    },
  },
  beforeDestroy() {
    this.stopCountdown()
    this.clearAutoLogout()
  },
}
</script>

<style scoped>
.inactivity-modal {
  width: 100%;
  max-width: 400px;
  min-width: 350px;
  @media (min-width: 400px) {
    min-width: 450px;
  }
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff; /* Fondo blanco */
  border-radius: 16px;
  color: #1a2a44; /* Azul oscuro para el texto */
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}
.icon-container {
  background: #f7fafc; /* Fondo gris muy claro */
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  border: 2px solid #f62d51; /* Amarillo suave */
}
.title.is-4 {
  color: #1a2a44; /* Azul oscuro */
}
.has-text-centered {
  color: #3a3a3a;
}
.buttons.is-centered .button {
  min-width: 120px;
  font-weight: 600;
}
.buttons.is-centered .button.is-danger {
  background: #ffeaea;
  color: #d7263d;
  border: none;
}
.buttons.is-centered .button.is-warning {
  background: #eaf6ff;
  color: #1a2a44;
  border: none;
}
.modal {
  z-index: 999999 !important;
}
::v-deep .animation-content {
  z-index: 9 !important;
}
.countdown-timer {
  font-size: 1.4rem;
  font-weight: 700;
  color: #f62d51;
  font-family: 'Courier New', monospace;
  background: #ffeaea;
  padding: 4px 8px;
  border-radius: 8px;
  display: inline-block;
  margin: 8px 0;
  box-shadow: 0 2px 8px rgba(246, 45, 81, 0.2);
  border: 2px solid #f62d51;
}
</style>
